import React, { useState, useRef, useCallback } from 'react';
import { Palette, Upload, Copy, Check, Download, RefreshCw } from 'lucide-react';

interface ColorInfo {
  hex: string;
  rgb: { r: number; g: number; b: number };
  hsl: { h: number; s: number; l: number };
  name: string;
  count: number;
}

type PaletteStyle = 'bar' | 'cards' | 'circles';
type ExportFormat = 'css' | 'json' | 'tailwind' | 'text';

const COLOR_NAMES: [number, number, number, string][] = [
  [0,0,0,'Black'],[255,255,255,'White'],[128,128,128,'Gray'],[255,0,0,'Red'],
  [0,255,0,'Lime'],[0,0,255,'Blue'],[255,255,0,'Yellow'],[0,255,255,'Cyan'],
  [255,0,255,'Magenta'],[128,0,0,'Maroon'],[0,128,0,'Green'],[0,0,128,'Navy'],
  [128,128,0,'Olive'],[128,0,128,'Purple'],[0,128,128,'Teal'],[255,165,0,'Orange'],
  [255,192,203,'Pink'],[165,42,42,'Brown'],[245,245,220,'Beige'],[255,215,0,'Gold'],
  [75,0,130,'Indigo'],[64,224,208,'Turquoise'],[250,128,114,'Salmon'],[220,20,60,'Crimson'],
  [47,79,79,'Dark Slate'],[119,136,153,'Light Slate'],[70,130,180,'Steel Blue'],
  [34,139,34,'Forest Green'],[255,99,71,'Tomato'],[255,140,0,'Dark Orange'],
];

function closestColorName(r: number, g: number, b: number): string {
  let minDist = Infinity, name = 'Unknown';
  for (const [cr, cg, cb, cn] of COLOR_NAMES) {
    const d = Math.sqrt((r - cr) ** 2 + (g - cg) ** 2 + (b - cb) ** 2);
    if (d < minDist) { minDist = d; name = cn; }
  }
  return name;
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('').toUpperCase();
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color);
  };
  return rgbToHex(f(0), f(8), f(4));
}

// Simple median-cut quantization
function quantizeColors(pixels: Uint8ClampedArray, count: number): ColorInfo[] {
  // Collect pixels, sampling every 4th for speed
  const buckets: number[][] = [];
  for (let i = 0; i < pixels.length; i += 16) { // every 4th pixel (4 channels)
    const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2], a = pixels[i + 3];
    if (a < 128) continue; // skip transparent
    buckets.push([r, g, b]);
  }

  if (buckets.length === 0) return [];

  // Median-cut
  function medianCut(box: number[][], depth: number): number[][][] {
    if (depth === 0 || box.length === 0) return [box];
    // Find channel with widest range
    let maxRange = 0, splitCh = 0;
    for (let ch = 0; ch < 3; ch++) {
      const vals = box.map(p => p[ch]);
      const range = Math.max(...vals) - Math.min(...vals);
      if (range > maxRange) { maxRange = range; splitCh = ch; }
    }
    box.sort((a, b) => a[splitCh] - b[splitCh]);
    const mid = Math.floor(box.length / 2);
    return [
      ...medianCut(box.slice(0, mid), depth - 1),
      ...medianCut(box.slice(mid), depth - 1),
    ];
  }

  const depthNeeded = Math.ceil(Math.log2(count));
  const groups = medianCut(buckets, depthNeeded);

  // Average each group
  const results: ColorInfo[] = groups
    .filter(g => g.length > 0)
    .map(group => {
      const avg = [0, 0, 0];
      for (const p of group) { avg[0] += p[0]; avg[1] += p[1]; avg[2] += p[2]; }
      const r = Math.round(avg[0] / group.length);
      const g = Math.round(avg[1] / group.length);
      const b = Math.round(avg[2] / group.length);
      return {
        hex: rgbToHex(r, g, b),
        rgb: { r, g, b },
        hsl: rgbToHsl(r, g, b),
        name: closestColorName(r, g, b),
        count: group.length,
      };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, count);

  return results;
}

const ColorPaletteExtractor: React.FC = () => {
  const [image, setImage] = useState<string | null>(null);
  const [colors, setColors] = useState<ColorInfo[]>([]);
  const [colorCount, setColorCount] = useState(5);
  const [paletteStyle, setPaletteStyle] = useState<PaletteStyle>('bar');
  const [copied, setCopied] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const extract = useCallback((imgSrc: string, count: number) => {
    setProcessing(true);
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const size = 100;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, size, size);
      const data = ctx.getImageData(0, 0, size, size).data;
      const result = quantizeColors(data, count);
      setColors(result);
      setProcessing(false);
    };
    img.src = imgSrc;
  }, []);

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    const url = URL.createObjectURL(file);
    setImage(url);
    extract(url, colorCount);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const copyText = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const copyAll = () => {
    const text = colors.map(c => c.hex).join('\n');
    copyText(text, 'all');
  };

  const exportPalette = (format: ExportFormat) => {
    let content = '';
    switch (format) {
      case 'css':
        content = ':root {\n' + colors.map((c, i) => `  --color-${i + 1}: ${c.hex};`).join('\n') + '\n}';
        break;
      case 'json':
        content = JSON.stringify(colors.map(c => ({ hex: c.hex, rgb: c.rgb, name: c.name })), null, 2);
        break;
      case 'tailwind':
        content = 'module.exports = {\n  theme: {\n    extend: {\n      colors: {\n' +
          colors.map((c, i) => `        'palette-${i + 1}': '${c.hex}',`).join('\n') +
          '\n      }\n    }\n  }\n}';
        break;
      case 'text':
        content = colors.map(c => `${c.hex} - ${c.name} - rgb(${c.rgb.r}, ${c.rgb.g}, ${c.rgb.b})`).join('\n');
        break;
    }
    navigator.clipboard.writeText(content);
    setCopied(format);
    setTimeout(() => setCopied(null), 1500);
  };

  const getComplementary = (h: number, s: number, l: number): string => hslToHex((h + 180) % 360, s, l);
  const getAnalogous = (h: number, s: number, l: number): [string, string] => [
    hslToHex((h + 30) % 360, s, l),
    hslToHex((h + 330) % 360, s, l),
  ];

  const reExtract = () => {
    if (image) extract(image, colorCount);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-pink-100 rounded-lg">
          <Palette className="w-6 h-6 text-pink-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Color Palette Extractor</h1>
          <p className="text-gray-500 text-sm">Extract dominant colors from any image</p>
        </div>
      </div>

      {/* Upload */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-pink-500 bg-pink-50' : 'border-gray-300 hover:border-pink-400'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">Drop an image here or click to upload</p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
        />
      </div>

      {image && (
        <div className="mt-6 space-y-6">
          {/* Controls */}
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mr-2">Colors:</label>
              {[3, 5, 8, 10].map(n => (
                <button
                  key={n}
                  onClick={() => { setColorCount(n); extract(image, n); }}
                  className={`px-3 py-1 text-sm rounded-md mr-1 ${
                    colorCount === n ? 'bg-pink-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mr-2">Style:</label>
              {(['bar', 'cards', 'circles'] as PaletteStyle[]).map(s => (
                <button
                  key={s}
                  onClick={() => setPaletteStyle(s)}
                  className={`px-3 py-1 text-sm rounded-md mr-1 capitalize ${
                    paletteStyle === s ? 'bg-pink-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <button onClick={reExtract} className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Re-extract
            </button>
          </div>

          {processing && <p className="text-gray-500 text-sm">Extracting colors...</p>}

          {/* Palette display */}
          {colors.length > 0 && !processing && (
            <>
              {paletteStyle === 'bar' && (
                <div className="rounded-xl overflow-hidden h-20 flex">
                  {colors.map((c, i) => (
                    <div
                      key={i}
                      className="flex-1 cursor-pointer group relative"
                      style={{ backgroundColor: c.hex }}
                      onClick={() => copyText(c.hex, c.hex)}
                    >
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                        <span className="text-white text-xs font-mono font-bold drop-shadow">{c.hex}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {paletteStyle === 'cards' && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {colors.map((c, i) => (
                    <div key={i} className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                      <div className="h-20" style={{ backgroundColor: c.hex }} />
                      <div className="p-2 bg-white text-xs space-y-1">
                        <p className="font-semibold text-gray-800">{c.name}</p>
                        <p className="font-mono text-gray-600">{c.hex}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {paletteStyle === 'circles' && (
                <div className="flex flex-wrap gap-4 justify-center">
                  {colors.map((c, i) => (
                    <div key={i} className="flex flex-col items-center gap-2">
                      <div
                        className="w-16 h-16 rounded-full border-2 border-white shadow-md cursor-pointer hover:scale-110 transition-transform"
                        style={{ backgroundColor: c.hex }}
                        onClick={() => copyText(c.hex, c.hex)}
                      />
                      <span className="text-xs font-mono text-gray-600">{c.hex}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Color details */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">Color Details</h3>
                <div className="grid gap-3">
                  {colors.map((c, i) => (
                    <div key={i} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 rounded-lg flex-shrink-0 border" style={{ backgroundColor: c.hex }} />
                      <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                        <div><span className="text-gray-400">HEX</span> <span className="font-mono block">{c.hex}</span></div>
                        <div><span className="text-gray-400">RGB</span> <span className="font-mono block">{c.rgb.r}, {c.rgb.g}, {c.rgb.b}</span></div>
                        <div><span className="text-gray-400">HSL</span> <span className="font-mono block">{c.hsl.h}, {c.hsl.s}%, {c.hsl.l}%</span></div>
                        <div><span className="text-gray-400">Name</span> <span className="block">{c.name}</span></div>
                      </div>
                      <button
                        onClick={() => copyText(c.hex, `copy-${i}`)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {copied === `copy-${i}` ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Harmony suggestions */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Harmony Suggestions</h3>
                <div className="space-y-2">
                  {colors.slice(0, 3).map((c, i) => {
                    const comp = getComplementary(c.hsl.h, c.hsl.s, c.hsl.l);
                    const [a1, a2] = getAnalogous(c.hsl.h, c.hsl.s, c.hsl.l);
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <div className="w-6 h-6 rounded border" style={{ backgroundColor: c.hex }} />
                        <span className="text-gray-400 w-24">Complement:</span>
                        <div className="w-6 h-6 rounded border" style={{ backgroundColor: comp }} />
                        <span className="font-mono text-gray-500">{comp}</span>
                        <span className="text-gray-400 ml-3 w-20">Analogous:</span>
                        <div className="w-6 h-6 rounded border" style={{ backgroundColor: a1 }} />
                        <div className="w-6 h-6 rounded border" style={{ backgroundColor: a2 }} />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={copyAll}
                  className="px-3 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 text-sm font-medium flex items-center gap-1"
                >
                  {copied === 'all' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  Copy All HEX
                </button>
                {(['css', 'json', 'tailwind', 'text'] as ExportFormat[]).map(f => (
                  <button
                    key={f}
                    onClick={() => exportPalette(f)}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-1"
                  >
                    {copied === f ? <Check className="w-4 h-4 text-green-500" /> : <Download className="w-4 h-4" />}
                    {f.toUpperCase()}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ColorPaletteExtractor;
